import { Injectable } from '@angular/core';
import { xor } from 'lodash-es';
import { catchError, distinctUntilChanged, EMPTY, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { LiveDeviceDiscoveryApiService } from '@shure/shared/angular/data-access/device-discovery';
import {
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { RequestResponse, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { RoomAdaptApiService } from '../api';
import { RoomAdaptDevice, RoomAdaptRoomDevice } from '../api/models';

import {
	AbortRoomAdaptMutationGQL,
	GetRoomAdaptDeviceQueryGQL,
	GetRoomAdaptRoomDevicesQueryGQL,
	RoomAdaptDeviceFragment,
	RoomAdaptDeviceSubscriptionGQL,
	StartRoomAdaptMutationGQL,
	RoomAdaptRoomDevicesSubscriptionGQL
} from './graphql/generated/room-adapt.generated';
import { mapRoomAdaptDeviceFromSysApi, mapRoomAdaptRoomDevicesFromSysApi } from './mappers';

@Injectable()
export class SysApiRoomAdaptApiService implements RoomAdaptApiService {
	private readonly logger: ILogger;
	private readonly deviceSubscriptionManager: CombiningSubscriptionManager;
	private readonly roomDevicesSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly getDeviceQueryGQL: GetRoomAdaptDeviceQueryGQL,
		private readonly startRoomAdaptMutationGQL: StartRoomAdaptMutationGQL,
		private readonly abortRoomAdaptMutationGQL: AbortRoomAdaptMutationGQL,
		private readonly getRoomDevicesQueryGQL: GetRoomAdaptRoomDevicesQueryGQL,
		private readonly deviceDiscoveryApiService: LiveDeviceDiscoveryApiService,
		deviceSubscriptionGQL: RoomAdaptDeviceSubscriptionGQL,
		roomDevicesSubscriptionGQL: RoomAdaptRoomDevicesSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('SysApiRoomAdaptApiService');
		this.deviceSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiRoomAdaptApiService:Device',
				changeTypes: [
					NodeChangeType.DeviceName,
					NodeChangeType.DeviceRoom,
					NodeChangeType.DeviceRoomAdapt,
					NodeChangeType.DeviceRoomCharacterization
				],
				subscriptionGql: deviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
		this.roomDevicesSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiRoomAdaptApiService:RoomDevices',
				changeTypes: [NodeChangeType.Room],
				subscriptionGql: roomDevicesSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<RoomAdaptDevice> {
		this.logger.trace('getDevice$()', 'Querying room adapt device', { deviceId });
		const queryId = uuidv4();

		return this.getDeviceQueryGQL.watch({ variables: { deviceId }, errorPolicy: 'all' }).valueChanges.pipe(
			waitForGQLCompleteQueryOrThrow(),
			map((result) => {
				if (result.data?.node) {
					if (result.error) {
						this.logger.trace('getDevice$()', 'Partial data returned for room adapt device', {
							deviceId,
							error: result.error.message
						});
					}
					const fragment = result.data.node as RoomAdaptDeviceFragment;
					return mapRoomAdaptDeviceFromSysApi(fragment);
				}
				throw ApolloQueryErrorMapper.getError(result);
			}),
			tapOnce(() => {
				this.deviceSubscriptionManager.subscribe(queryId, deviceId);
			}),
			noMoreSubscribers(() => {
				this.deviceSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query room adapt device', {
					deviceId,
					error: error.message
				});
				return throwError(() => error);
			})
		);
	}

	public getDevicesInRoom$(roomId: string): Observable<RoomAdaptRoomDevice[]> {
		const queryId = uuidv4();
		this.logger.trace('getDevicesInRoom$()', 'Querying room adapt devices in room', { roomId, queryId });

		return this.getRoomDevicesQueryGQL.watch({ variables: { id: roomId }, errorPolicy: 'all' }).valueChanges.pipe(
			waitForGQLCompleteQueryOrThrow(),
			map((result) => {
				if (!result.data?.node || !('devices' in result.data.node)) {
					throw ApolloQueryErrorMapper.getError(result);
				}
				return mapRoomAdaptRoomDevicesFromSysApi(result.data.node);
			}),
			tapOnce(() => this.roomDevicesSubscriptionManager.subscribe(queryId, roomId)),
			noMoreSubscribers(() => this.roomDevicesSubscriptionManager.unsubscribeQuery(queryId)),
			catchError((error: Error) => {
				this.logger.error('getDevicesInRoom$()', 'Failed to query devices in room', {
					roomId,
					error: error.message
				});
				return throwError(() => error);
			})
		);
	}

	public getAllRoomAdaptDeviceIds$(): Observable<string[]> {
		return this.deviceDiscoveryApiService
			.getDiscoveredDevicesByQuery$<RoomAdaptDevice>((id) => this.getDevice$(id), { throttleMs: 10 })
			.pipe(
				map((devices) =>
					devices
						.filter(
							(device) =>
								!!device.features.roomCharacterization &&
								!device.features.roomCharacterization.isMissing
						)
						.map((device) => device.id)
				),
				distinctUntilChanged((a, b) => xor(a, b).length === 0),
				catchError((error: Error) => {
					this.logger.error(
						'getAllRoomAdaptDeviceIds$()',
						'Failed to query room-adapt-capable devices online',
						{ error: error.message }
					);
					return of([]);
				}),
				shareReplay({ refCount: true, bufferSize: 1 })
			);
	}

	public startRoomAdapt(deviceId: string): Observable<RequestResponse<string>> {
		this.logger.trace('startRoomAdapt()', 'Starting room adapt', { deviceId });

		return this.startRoomAdaptMutationGQL
			.mutate({ variables: { id: deviceId, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(
				map((result) => {
					if (result.data?.startRoomCharacterization.success) {
						return { state: ResponseState.Done };
					}
					const errorMessage =
						result.data?.startRoomCharacterization.error?.message ??
						result.error?.message ??
						'Unknown error';
					this.logger.error('startRoomAdapt()', 'Error starting room characterization', {
						deviceId,
						error: errorMessage
					});
					return { state: ResponseState.Error, error: errorMessage };
				}),
				catchError((error: Error) => {
					this.logger.error('startRoomAdapt()', 'Failed to start room characterization', {
						deviceId,
						error: error.message
					});
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public abortRoomAdapt(deviceId: string): void {
		this.logger.trace('abortRoomAdapt()', 'Not yet implemented', { deviceId });

		this.abortRoomAdaptMutationGQL
			.mutate({ variables: { id: deviceId, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(
				map((result) => {
					if (result.data?.abortRoomCharacterization.success) {
						this.logger.trace('abortRoomAdapt()', 'Successfully aborted room adapt', {
							deviceId
						});
					} else {
						const errorMessage =
							result.data?.abortRoomCharacterization.error?.message ??
							result.error?.message ??
							'Unknown error';
						this.logger.error('abortRoomAdapt()', 'Error aborting room adapt', {
							deviceId,
							error: errorMessage
						});
					}
				}),
				catchError((error: Error) => {
					this.logger.error('abortRoomAdapt()', 'Failed to abort room adapt', {
						deviceId,
						error: error.message
					});
					return EMPTY;
				})
			)
			.subscribe();
	}

	public retrieveRoomCharacterizationReport(deviceId: string): Observable<string | null> {
		// TODO: (DNG-9766) Implement in summary dialog task
		this.logger.trace('retrieveRoomCharacterizationReport()', 'Not yet implemented', { deviceId });
		return of(null);
	}
}
